import { useCallback, useMemo, useRef, useState } from 'react';
import { get } from 'lodash';
import { useAppDispatch, useAppSelector } from 'slices/store';
import {
  type IImportUser,
  importErrorSelector,
  importProgressSelector,
  importUsers,
} from 'slices/user.slice';
import { errorNotification } from 'utils/notifications';
import {
  type EUserVisibility,
  type TAttributesAndValuesPair,
  exportedUserColumns,
} from '@timeedit/types';
import { Button } from '@timeedit/ui-components';
import {
  Modal,
  Progress,
  Space,
  Steps,
  type UploadFile,
} from '@timeedit/ui-components/antd';
import { type OnFileRead, UploadUser } from './UploadUser';
import { UserPreview } from './UserPreview';
import { parseInactiveSince } from './utils/parseInactiveSince';
import './ModalImport.scss';

interface UserImportProps {
  isModalOpen: boolean;
  setIsModalOpen: (val: boolean) => void;
}

export const UserImport = ({
  isModalOpen,
  setIsModalOpen,
}: UserImportProps) => {
  const dispatch = useAppDispatch();
  const [userData, setUserData] = useState<UserPreview[]>([]);
  const [uploadedColumns, setUploadedColumns] = useState<string[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const fileListRef = useRef<UploadFile[]>([]);

  const applyFileList = useCallback((nextFileList: UploadFile[]) => {
    fileListRef.current = nextFileList;
    setFileList(nextFileList);
  }, []);

  const importProgress = useAppSelector(importProgressSelector);
  const importError = useAppSelector(importErrorSelector);

  const [current, setCurrent] = useState(0);
  const [authConfigId, setAuthConfigId] = useState<string>();
  const [isImporting, setIsImporting] = useState(false);

  const handleOk = useCallback(async () => {
    if (!authConfigId) {
      return errorNotification('Select Auth Config');
    }

    const hasInactiveSince = uploadedColumns.includes(
      exportedUserColumns.inactiveSince
    );

    try {
      setCurrent(2);
      setIsImporting(true);
      await dispatch(
        importUsers(authConfigId, createImportUsers(userData), {
          hasInactiveSince,
        })
      );
    } finally {
      setIsImporting(false);
    }
  }, [authConfigId, dispatch, uploadedColumns, userData]);

  const finishImport = useCallback(() => {
    setUserData([]);
    setUploadedColumns([]);
    applyFileList([]);
    setAuthConfigId(undefined);
    setIsModalOpen(false);
    setCurrent(0);
  }, [setIsModalOpen, applyFileList]);

  const onFileListChange = useCallback(
    (nextFileList: UploadFile[]) => {
      applyFileList(nextFileList);

      if (nextFileList.length === 0) {
        setUserData([]);
        setUploadedColumns([]);
      }
    },
    [applyFileList]
  );

  const onFileRead = useCallback<OnFileRead>((result) => {
    if (!fileListRef.current.some((file) => file.uid === result.uid)) {
      return;
    }

    if (result.kind === 'failed') {
      errorNotification(result.message, result.details);
      return;
    }

    setCurrent(1);
    setUserData(result.data);
    setUploadedColumns(result.columns);
  }, []);

  const steps = useMemo(() => {
    return [
      {
        title: 'Upload File',
        content: (
          <UploadUser
            onFileRead={onFileRead}
            fileList={fileList}
            onFileListChange={onFileListChange}
          />
        ),
      },
      {
        title: 'Preview & Import',
        content: (
          <UserPreview
            data={userData}
            columns={uploadedColumns}
            authConfigId={authConfigId}
            setAuthConfigId={setAuthConfigId}
          />
        ),
      },
      {
        title: 'Progress',
        content: (
          <div
            style={{
              width: '100%',
              marginTop: 90,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: 30,
              }}
            >
              <Progress
                status={importError ? 'exception' : 'success'}
                percent={importProgress}
                type="circle"
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                fontSize: 17,
              }}
            >
              {importProgress === 100 ? (
                <span>
                  See results in{' '}
                  <a href="/user-management/imports">
                    <strong>User Management &gt; Logs &gt; Imports</strong>{' '}
                  </a>
                  page
                </span>
              ) : (
                <span>{importError?.message || 'Importing users...'}</span>
              )}
            </div>
          </div>
        ),
      },
    ];
  }, [
    authConfigId,
    fileList,
    importError,
    importProgress,
    onFileListChange,
    onFileRead,
    uploadedColumns,
    userData,
  ]);

  const items = useMemo(
    () => steps.map((item) => ({ key: item.title, title: item.title })),
    [steps]
  );

  return (
    <Modal
      title="Import User"
      open={isModalOpen}
      width={'80%'}
      closable={!isImporting}
      maskClosable={!isImporting}
      footer={
        <Space>
          {current === 0 && (
            <Button onClick={() => finishImport()}>Cancel</Button>
          )}
          {current === 1 && (
            <>
              <Button onClick={() => setCurrent(current - 1)}>Previous</Button>
              <Button type="primary" onClick={handleOk}>
                Import
              </Button>
            </>
          )}
          {current === 2 && (
            <Button
              type="primary"
              disabled={isImporting}
              onClick={finishImport}
            >
              OK
            </Button>
          )}
        </Space>
      }
      onCancel={isImporting ? undefined : finishImport}
    >
      <div style={{ minHeight: '50vh' }}>
        <Steps current={current} items={items} />
        {steps[current].content}
      </div>
    </Modal>
  );
};

function createImportUsers(users: UserPreview[]): IImportUser[] {
  return users.map((user) => ({
    id: get(user, exportedUserColumns.id),
    profileName: get(user, exportedUserColumns.profileName, ''),
    email: get(user, exportedUserColumns.email, ''),
    username: get(user, exportedUserColumns.username, ''),
    firstName: get(user, exportedUserColumns.firstName),
    lastName: get(user, exportedUserColumns.lastName),
    contactEmail: get(user, exportedUserColumns.contactEmail),
    userObjects: get(user, exportedUserColumns.userObjects)?.split(', ') || [],
    standardOrganization: get(user, exportedUserColumns.standardOrganization),
    organizationsAndRoles: ((get(
      user,
      exportedUserColumns.organizationsAndRoles
    )
      ?.split('; ')
      .map((orgRoles: string) =>
        orgRoles.split('|')
      ) as TAttributesAndValuesPair) || []) as TAttributesAndValuesPair[],
    scopes: get(user, exportedUserColumns.scopes)?.split(', ') || [],
    appPermissions:
      get(user, exportedUserColumns.appPermissions)?.split(', ') || [],
    language: get(user, exportedUserColumns.language),
    inactiveSince: parseInactiveSince(
      get(user, exportedUserColumns.inactiveSince)
    ),
    visibility: get(user, exportedUserColumns.visibility) as EUserVisibility,
    description: get(user, exportedUserColumns.description),
    lockedForExternalAuthentication:
      get(user, exportedUserColumns.lockedForExternalAuthentication) === 'Yes',
  }));
}

export default UserImport;
